import React, { useRef, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Table } from '@/components/design-system/molecules/table';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { ColocAPIConnectedTable } from '@/features/shared/feature-coloc-api-connected-table';
import { ContractsListProvider, useContractsListContext } from '../../hooks/use-get-contracts-list';
import { ColocAPIConnectedTableImperativeMethods } from '@/features/shared/feature-coloc-api-connected-table/types';
import type { Contract } from '../../types';
import { ContractsListFilter } from './components/contracts-list-filter';
import { ContractsListFiltersProvider } from '../../hooks/use-get-contracts-list-filter';
import { useDiscussionThreadProviderContext } from '@/features/shared/discussion-threads/hooks/use-discussion-thread';
import { DiscussionThreadTableCellContent } from '@/features/shared/discussion-threads/components/discussion-thread-table-cell-content';
import { AxiosResponse } from 'axios';
import { SepaStatus } from '../../types';
import { ResidencyStatus } from '../../types';
import {
  ContractStatus,
  ContractStatusCellContent,
} from './components/contract-status-cell-content';
import { composeClassName } from '@/utils/util_helpers';
import { theme } from '@/utils/util_theme';
import { ContractsListSortType } from '@/lib/coloc-api/admin';
import ContractDiscountRow from './components/contract-discount-row';
import { useGetContractsCSV } from './hooks/use-get-contracts-csv';
import { ContractsSortingProvider, useContractsSortingContext } from '../../hooks/use-contracts-sorting-context';

const columnHelper = createColumnHelper<Contract>();

const formatCurrency = (cents: number) => {
  const euros = (cents / 100).toFixed(2);
  return `€${euros}`;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'MMM d, yyyy');
};

export const ContractsListContent = () => {
  const tableRef = useRef<ColocAPIConnectedTableImperativeMethods>(null);
  const { t } = useTranslation();
  const {
    fetch,
    updateContractInCache,
    sortableColumns,
  } = useContractsListContext();
  const { openDiscussionThread } = useDiscussionThreadProviderContext()!;
  const { fetch: fetchCSV, isExportingContracts } = useGetContractsCSV();
  const {
    sortDirection,
    setSortDirection,
    selectedColumnToSort,
    setColumnToSort,
  } = useContractsSortingContext();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => ({ id: row.id, verified: row.coloc_verified }), {
        id: 'id',
        header: () => (
          <div className="pl-3">{t('js.features.admin.contracts.list.columns.id')}</div>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className="flex items-center gap-2 pl-3">
              <span className="relative -left-[2px] top-[2px]">
                {value.verified ? (
                  <img
                    src={require('@assets/images/icons/circle-checkmark-green-bg.svg')}
                    alt="verified"
                  />
                ) : (
                  <img
                    src={require('@assets/images/icons/circle-cross-red-bg-small.svg')}
                    alt="not verified"
                  />
                )}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor(
        (row) => ({
          colocId: row.coloc.id,
          name: row.coloc.name,
          room: row.room_number,
          colocation_name: row.colocation.name,
        }),
        {
          id: 'room_number',
          header: () => t('js.features.admin.contracts.list.columns.tenant_and_room'),
          cell: (info) => {
            const value = info.getValue();
            return (
              <a
                className="flex flex-col"
                href={`/admin/colocs/${value.colocId}`}
                target="_blank"
                onClick={e => e.stopPropagation()}
              >
                <div>
                  <span className="truncate font-medium text-N900 hover:underline">
                    {value.name}
                  </span>
                </div>
                <span className="truncate text-[14px] text-text-primaryLight">
                  {value.colocation_name} - #{value.room}
                </span>
              </a>
            );
          },
          enableSorting: true,
        }
      ),
      columnHelper.accessor('discussion_thread', {
        id: 'discussion_thread',
        cell: (info) => (
          <div onClick={(e) => e.stopPropagation()}>
            <DiscussionThreadTableCellContent
              messages_count={info.getValue()?.messages_count || 0}
              onClick={() => {
                openDiscussionThread({
                  type: 'Contract',
                  ...(info.getValue()?.id && info.getValue()?.id !== 'null'
                    ? { discussionThreadId: Number(info.getValue()?.id) }
                    : { discussableId: Number(info.row.original.id) }),
                  onMessageSent: () => {
                    fetch.refetch().then((response: AxiosResponse<any>) => {
                      if (response.data) {
                        console.log(JSON.stringify(response.data));
                        updateContractInCache(response.data);
                      }
                    });
                  },
                });
              }}
            />
          </div>
        ),
        header: () =>
          t('js.features.admin.contracts.room_assignment.table_columns.discussion_thread'),
        footer: (info) => info.column.id,
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          className: 'w-[50px]',
          headerClassName: 'w-[50px]',
        },
      }),
      columnHelper.accessor('status', {
        header: () => t('js.features.admin.contracts.list.columns.status'),
        cell: (info) => <ContractStatusCellContent status={info.getValue() as ContractStatus} />,
        enableSorting: true,
        id: 'status',
      }),
      columnHelper.accessor('full_rent', {
        header: () => t('js.features.admin.contracts.list.columns.rent'),
        cell: (info) => formatCurrency(info.getValue().cents),
        enableSorting: true,
        id: 'due_amount',
      }),
      columnHelper.accessor('move_in', {
        header: () => t('js.features.admin.contracts.list.columns.move_in'),
        cell: (info) => formatDate(info.getValue()),
        enableSorting: true,
        id: 'move_in',
      }),
      columnHelper.accessor('move_out', {
        header: () => t('js.features.admin.contracts.list.columns.move_out'),
        cell: (info) => (info.getValue() ? formatDate(info.getValue()) : '-'),
        enableSorting: true,
        id: 'move_out',
      }),
      columnHelper.accessor('residency_registration_status', {
        header: () => t('js.features.admin.contracts.list.columns.residency'),
        cell: (info) => {
          return (
            <div className="inline-block">
              {ResidencyStatus[info.getValue() as keyof typeof ResidencyStatus] && (
                <div
                  className={composeClassName(
                    'flex items-center gap-1 rounded-[19px] border-[1px] border-solid px-2 py-1',
                    'border-E75',
                    'bg-E30'
                  )}
                >
                  <span
                    className={composeClassName('text-body')}
                    style={{ color: theme.colors.text.N900 }}
                  >
                    {ResidencyStatus[info.getValue() as keyof typeof ResidencyStatus]}
                  </span>
                </div>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('sepa_status', {
        header: () => t('js.features.admin.contracts.list.columns.sepa'),
        cell: (info) => {
          return (
            <div className="inline-block">
              <div
                className={composeClassName(
                  'flex items-center gap-1 rounded-[19px] border-[1px] border-solid px-2 py-1',
                  'border-E75',
                  'bg-E30'
                )}
              >
                <span
                  className={composeClassName('text-body')}
                  style={{ color: theme.colors.text.N900 }}
                >
                  {info.getValue()
                    ? `${SepaStatus[info.getValue() as keyof typeof SepaStatus]}`
                    : '-'}
                </span>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row.colocation.property_manager, {
        id: 'property_manager',
        header: () => t('js.features.admin.contracts.list.columns.property_manager'),
        cell: (info) => info.getValue()?.name || '-',
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Table.TableCellRowActionsContent>
              <Table.TableCellRowActionsContent.TableRowActions>
                <Table.TableCellRowActionsContent.TableRowAction
                  onClick={() => {
                    window.open(`/admin/contracts/${row.original.id}`, '_blank');
                  }}
                >
                  <Table.TableCellRowActionsContent.TableRowActionContent
                    contentBefore={<IconSaxIcon name="eye" size={20} />}
                  >
                    View contract
                  </Table.TableCellRowActionsContent.TableRowActionContent>
                </Table.TableCellRowActionsContent.TableRowAction>

                <Table.TableCellRowActionsContent.TableRowAction
                  onClick={() => {
                    window.open(`/admin/contracts/${row.original.id}/edit`, '_blank');
                  }}
                >
                  <Table.TableCellRowActionsContent.TableRowActionContent
                    contentBefore={<IconSaxIcon name="edit" size={20} />}
                  >
                    Edit contract
                  </Table.TableCellRowActionsContent.TableRowActionContent>
                </Table.TableCellRowActionsContent.TableRowAction>

                <Table.TableCellRowActionsContent.TableRowAction
                  onClick={() => {
                    window.open(
                      `/admin/new-rents?all=true&house_id=${
                        row.original.colocation_id
                      }&house_name=${encodeURIComponent(
                        row.original.colocation.name
                      )}&start_date=${row.original.move_in}&tenant_name=${encodeURIComponent(row.original.coloc.name)}`,
                      '_blank'
                    );
                  }}
                >
                  <Table.TableCellRowActionsContent.TableRowActionContent
                    contentBefore={<IconSaxIcon name="money" size={20} />}
                  >
                    Show rents
                  </Table.TableCellRowActionsContent.TableRowActionContent>
                </Table.TableCellRowActionsContent.TableRowAction>

                <Table.TableCellRowActionsContent.TableRowAction
                  onClick={() => row.toggleExpanded()}
                >
                  <Table.TableCellRowActionsContent.TableRowActionContent
                    contentBefore={<IconSaxIcon name="discount-shape" size={20} />}
                  >
                    Discount
                  </Table.TableCellRowActionsContent.TableRowActionContent>
                </Table.TableCellRowActionsContent.TableRowAction>
              </Table.TableCellRowActionsContent.TableRowActions>
            </Table.TableCellRowActionsContent>
          </div>
        ),
        footer: (info) => info.column.id,
      }),
    ],
    [t]
  );

  return (
    <div className="z-0 flex flex-1 flex-col overflow-hidden">
      <ColocAPIConnectedTable<Contract, ContractsListSortType>
        ref={tableRef}
        columns={columns}
        dataSource={{
          ...useContractsListContext()!,
          sortDirection,
          setSortDirection,
          selectedColumnToSort,
          setColumnToSort,
          sortableColumns,
        }}
        renderSubComponent={(row) => (
          <div
            className="max-h-[400px] overflow-y-auto relative"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="sticky top-0 z-10 bg-white">
              <ContractDiscountRow
                contract={row.row.original}
                index={row.row.index}
                onClose={() => row.row.toggleExpanded()}
              />
            </div>
          </div>
        )}
        onRowClick={(row) => {
          window.open(`/admin/contracts/${row.original.id}`, '_blank');
        }}
      />

      <div className="flex justify-end pt-2 pb-2 mb-7">
        <button
          className="flex h-[40px] items-center justify-center rounded-[8px] bg-R300 px-[18px] text-body font-medium text-text-primary hover:bg-R400"
          onClick={() => fetchCSV()}
          disabled={isExportingContracts}
        >
          {isExportingContracts ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
    </div>
  );
};

const Contracts = () => {
  return (
    <ContractsListFiltersProvider>
      <ContractsSortingProvider>
        <ContractsListProvider>
          <div className="flex h-full w-full flex-1 flex-col text-[14px] leading-[21px]">
            <ContractsListFilter />
            <ContractsListContent />
          </div>
        </ContractsListProvider>
      </ContractsSortingProvider>
    </ContractsListFiltersProvider>
  );
};

export default Contracts;
